import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Plus, Pencil, Trash2, Search, Calendar, BookOpen, Image, Copy, Upload, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';

export const AdminCourseTemplatesPage = () => {
  const { t, language } = useLanguage();
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    title_ar: '',
    category_id: '',
    description: '',
    description_ar: '',
    objectives: '',
    objectives_ar: '',
    outline: '',
    outline_ar: '',
    daily_outline: [],
    who_should_attend: '',
    who_should_attend_ar: '',
    language: 'english',
    duration_days: '',
    duration_hours: '',
    base_price: '',
    currency: 'EUR',
    course_image: '',
    is_active: true,
  });
  const [uploadingImage, setUploadingImage] = useState(false);
  const imageInputRef = useRef(null);

  useEffect(() => {
    fetchTemplates();
    fetchCategories();
  }, []);

  // Upload course image
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only JPG, PNG, GIF, and WEBP images are allowed');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setUploadingImage(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await fetch(`${API}/upload/course-image`, {
        method: 'POST',
        credentials: 'include',
        body: formDataUpload
      });

      if (response.ok) {
        const data = await response.json();
        handleChange('course_image', data.url);
        toast.success('Image uploaded successfully');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to upload image');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
      if (imageInputRef.current) {
        imageInputRef.current.value = '';
      }
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await fetch(`${API}/admin/course-templates`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API}/categories`);
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const openDialog = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setFormData({
        title: template.title || '',
        title_ar: template.title_ar || '',
        category_id: template.category_id || '',
        description: template.description || '',
        description_ar: template.description_ar || '',
        objectives: template.objectives?.join('\n') || '',
        objectives_ar: template.objectives_ar?.join('\n') || '',
        outline: template.outline?.join('\n') || '',
        outline_ar: template.outline_ar?.join('\n') || '',
        daily_outline: template.daily_outline || [],
        who_should_attend: template.who_should_attend || '',
        who_should_attend_ar: template.who_should_attend_ar || '',
        language: template.language || 'english',
        duration_days: template.duration_days?.toString() || '',
        duration_hours: template.duration_hours?.toString() || '',
        base_price: template.base_price?.toString() || '',
        currency: template.currency || 'EUR',
        course_image: template.course_image || '',
        is_active: template.is_active !== false,
      });
    } else {
      setEditingTemplate(null);
      setFormData({
        title: '',
        title_ar: '',
        category_id: '',
        description: '',
        description_ar: '',
        objectives: '',
        objectives_ar: '',
        outline: '',
        outline_ar: '',
        daily_outline: [],
        who_should_attend: '',
        who_should_attend_ar: '',
        language: 'english',
        duration_days: '',
        duration_hours: '',
        base_price: '',
        currency: 'EUR',
        course_image: '',
        is_active: true,
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const payload = {
      ...formData,
      duration_days: parseInt(formData.duration_days) || 1,
      duration_hours: formData.duration_hours ? parseInt(formData.duration_hours) : null,
      base_price: formData.base_price ? parseFloat(formData.base_price) : null,
      objectives: formData.objectives.split('\n').filter(Boolean),
      objectives_ar: formData.objectives_ar.split('\n').filter(Boolean),
      outline: formData.outline.split('\n').filter(Boolean),
      outline_ar: formData.outline_ar.split('\n').filter(Boolean),
      daily_outline: formData.daily_outline,
    };

    try {
      const url = editingTemplate 
        ? `${API}/admin/course-templates/${editingTemplate.template_id}`
        : `${API}/admin/course-templates`;
      
      const response = await fetch(url, {
        method: editingTemplate ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        toast.success(editingTemplate ? 'Template updated successfully' : 'Template created successfully');
        setDialogOpen(false);
        fetchTemplates();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to save template');
      }
    } catch (error) {
      toast.error('Failed to save template');
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Are you sure? This cannot be undone if no sessions exist.')) return;

    try {
      const response = await fetch(`${API}/admin/course-templates/${templateId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        toast.success('Template deleted successfully');
        fetchTemplates();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to delete template');
      }
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  // Daily Outline Management
  const addDay = () => {
    const newDay = {
      day: formData.daily_outline.length + 1,
      title: '',
      title_ar: '',
      topics: [],
      topics_ar: [],
    };
    setFormData(prev => ({
      ...prev,
      daily_outline: [...prev.daily_outline, newDay]
    }));
  };

  const removeDay = (dayIndex) => {
    setFormData(prev => ({
      ...prev,
      daily_outline: prev.daily_outline
        .filter((_, i) => i !== dayIndex)
        .map((day, i) => ({ ...day, day: i + 1 }))
    }));
  };

  const updateDay = (dayIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      daily_outline: prev.daily_outline.map((day, i) => 
        i === dayIndex ? { ...day, [field]: value } : day
      )
    }));
  };

  const updateDayTopics = (dayIndex, field, value) => {
    const topics = value.split('\n').filter(Boolean);
    updateDay(dayIndex, field, topics);
  };

  const filteredTemplates = templates.filter(template => 
    template.title.toLowerCase().includes(search.toLowerCase()) ||
    template.title_ar.includes(search)
  );

  return (
    <div className="p-6" data-testid="admin-course-templates-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#111827]">Course Templates</h1>
          <p className="text-[#6B7280]">Master course content (reusable across sessions)</p>
        </div>
        <Button className="btn-primary" onClick={() => openDialog()} data-testid="add-template-btn">
          <Plus className="h-4 w-4 me-2" />
          New Template
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="relative">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B7280]" />
            <Input
              placeholder="Search templates..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="ps-9 max-w-sm"
              data-testid="search-input"
            />
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#33AADD] mx-auto"></div>
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="text-center py-8">
              <BookOpen className="h-12 w-12 text-[#E5E7EB] mx-auto mb-3" />
              <p className="text-[#6B7280]">No course templates found</p>
              <p className="text-sm text-[#9CA3AF]">Create a template to start scheduling sessions</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Template</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Base Price</TableHead>
                    <TableHead>Sessions</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-end">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTemplates.map((template, index) => (
                    <TableRow key={template.template_id} data-testid={`template-row-${index}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          {template.course_image && (
                            <img 
                              src={template.course_image.startsWith('/') ? `${API.replace('/api', '')}${template.course_image}` : template.course_image} 
                              alt="" 
                              className="w-12 h-8 object-cover rounded"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          )}
                          <div>
                            <p className="font-medium text-[#111827]">
                              {language === 'ar' ? template.title_ar : template.title}
                            </p>
                            <Badge variant="outline" className="text-xs mt-1">{template.language}</Badge>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{template.duration_days} days</TableCell>
                      <TableCell>
                        {template.base_price ? `${template.currency} ${template.base_price}` : '-'}
                      </TableCell>
                      <TableCell>
                        <Badge className="bg-[#33AADD]">{template.session_count || 0}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={template.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                          {template.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-end">
                        <Button variant="ghost" size="sm" onClick={() => openDialog(template)} title="Edit">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(template.template_id)} title="Delete">
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Template Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTemplate ? 'Edit' : 'New'} Course Template</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <Tabs defaultValue="basic" className="w-full">
              <TabsList className="grid w-full grid-cols-3 mb-4">
                <TabsTrigger value="basic">Basic Info</TabsTrigger>
                <TabsTrigger value="content">Content</TabsTrigger>
                <TabsTrigger value="schedule">
                  <Calendar className="h-4 w-4 me-1" />
                  Daily Schedule
                </TabsTrigger>
              </TabsList>

              <TabsContent value="basic" className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="title">Title (English) *</Label>
                    <Input id="title" value={formData.title} onChange={(e) => handleChange('title', e.target.value)} required />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="title_ar">Title (Arabic) *</Label>
                    <Input id="title_ar" value={formData.title_ar} onChange={(e) => handleChange('title_ar', e.target.value)} required dir="rtl" />
                  </div>
                </div>

                <div className="grid sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Select value={formData.category_id} onValueChange={(v) => handleChange('category_id', v)}>
                      <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                      <SelectContent>
                        {categories.map((cat) => (
                          <SelectItem key={cat.category_id} value={cat.category_id}>{cat.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Language</Label>
                    <Select value={formData.language} onValueChange={(v) => handleChange('language', v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="english">English</SelectItem>
                        <SelectItem value="arabic">Arabic</SelectItem>
                        <SelectItem value="bilingual">Bilingual</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2 pt-6">
                    <input 
                      type="checkbox" 
                      id="is_active" 
                      checked={formData.is_active} 
                      onChange={(e) => handleChange('is_active', e.target.checked)}
                      className="rounded"
                    />
                    <Label htmlFor="is_active">Active</Label>
                  </div>
                </div>

                <div className="grid sm:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="duration_days">Duration (Days) *</Label>
                    <Input id="duration_days" type="number" min="1" value={formData.duration_days} onChange={(e) => handleChange('duration_days', e.target.value)} required />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="duration_hours">Total Hours</Label>
                    <Input id="duration_hours" type="number" min="1" value={formData.duration_hours} onChange={(e) => handleChange('duration_hours', e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="base_price">Base Price</Label>
                    <Input id="base_price" type="number" min="0" step="0.01" value={formData.base_price} onChange={(e) => handleChange('base_price', e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Currency</Label>
                    <Select value={formData.currency} onValueChange={(v) => handleChange('currency', v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="EUR">EUR</SelectItem>
                        <SelectItem value="USD">USD</SelectItem>
                        <SelectItem value="AED">AED</SelectItem>
                        <SelectItem value="SAR">SAR</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-4 p-4 bg-[#F9FAFB] rounded-lg">
                  <div className="flex items-center gap-2">
                    <Image className="h-4 w-4 text-[#33AADD]" />
                    <Label className="font-medium">Course Cover Image</Label>
                  </div>
                  
                  {/* Upload Button */}
                  <div className="space-y-2">
                    <input
                      ref={imageInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      onChange={handleImageUpload}
                      className="hidden"
                      id="course-image-upload"
                    />
                    <div className="flex items-center gap-3">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => imageInputRef.current?.click()}
                        disabled={uploadingImage}
                        className="flex items-center gap-2"
                      >
                        {uploadingImage ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload className="h-4 w-4" />
                            Upload Image
                          </>
                        )}
                      </Button>
                      <span className="text-xs text-[#6B7280]">JPG, PNG, GIF, WEBP (max 5MB)</span>
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-gray-200" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-[#F9FAFB] px-2 text-gray-500">Or enter URL manually</span>
                    </div>
                  </div>

                  {/* Manual URL Input */}
                  <div className="space-y-2">
                    <Label htmlFor="course_image">Image URL</Label>
                    <Input 
                      id="course_image" 
                      value={formData.course_image} 
                      onChange={(e) => handleChange('course_image', e.target.value)} 
                      placeholder="https://example.com/image.jpg or /api/images/..."
                    />
                  </div>

                  {/* Image Preview */}
                  {formData.course_image && (
                    <div>
                      <p className="text-sm text-[#6B7280] mb-2">Preview:</p>
                      <img 
                        src={formData.course_image.startsWith('/') ? `${API.replace('/api', '')}${formData.course_image}` : formData.course_image} 
                        alt="Course preview" 
                        className="max-w-xs h-32 object-cover rounded-lg border"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </div>
                  )}
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="description">Description (English) *</Label>
                    <Textarea id="description" value={formData.description} onChange={(e) => handleChange('description', e.target.value)} rows={3} required />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="description_ar">Description (Arabic) *</Label>
                    <Textarea id="description_ar" value={formData.description_ar} onChange={(e) => handleChange('description_ar', e.target.value)} rows={3} required dir="rtl" />
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="content" className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="who_should_attend">Who Should Attend (English) *</Label>
                    <Textarea id="who_should_attend" value={formData.who_should_attend} onChange={(e) => handleChange('who_should_attend', e.target.value)} rows={2} required />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="who_should_attend_ar">Who Should Attend (Arabic) *</Label>
                    <Textarea id="who_should_attend_ar" value={formData.who_should_attend_ar} onChange={(e) => handleChange('who_should_attend_ar', e.target.value)} rows={2} required dir="rtl" />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Objectives (English, one per line)</Label>
                    <Textarea value={formData.objectives} onChange={(e) => handleChange('objectives', e.target.value)} rows={4} />
                  </div>
                  <div className="space-y-2">
                    <Label>Objectives (Arabic, one per line)</Label>
                    <Textarea value={formData.objectives_ar} onChange={(e) => handleChange('objectives_ar', e.target.value)} rows={4} dir="rtl" />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Topic Outline (English, one per line)</Label>
                    <Textarea value={formData.outline} onChange={(e) => handleChange('outline', e.target.value)} rows={4} />
                  </div>
                  <div className="space-y-2">
                    <Label>Topic Outline (Arabic, one per line)</Label>
                    <Textarea value={formData.outline_ar} onChange={(e) => handleChange('outline_ar', e.target.value)} rows={4} dir="rtl" />
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="schedule" className="space-y-4">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h3 className="font-semibold text-[#111827]">Daily Breakdown</h3>
                    <p className="text-sm text-[#6B7280]">Detailed schedule for each training day</p>
                  </div>
                  <Button type="button" onClick={addDay} variant="outline">
                    <Plus className="h-4 w-4 me-2" />
                    Add Day
                  </Button>
                </div>

                {formData.daily_outline.length === 0 ? (
                  <div className="text-center py-8 border-2 border-dashed border-[#E5E7EB] rounded-lg">
                    <Calendar className="h-12 w-12 text-[#9CA3AF] mx-auto mb-3" />
                    <p className="text-[#6B7280]">No daily schedule added</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {formData.daily_outline.map((day, dayIndex) => (
                      <Card key={dayIndex} className="border-s-4 border-s-[#33AADD]">
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start mb-4">
                            <div className="flex items-center gap-2">
                              <span className="w-8 h-8 rounded-full bg-[#33AADD] text-white flex items-center justify-center text-sm font-bold">{day.day}</span>
                              <span className="font-semibold">Day {day.day}</span>
                            </div>
                            <Button type="button" variant="ghost" size="sm" onClick={() => removeDay(dayIndex)} className="text-red-500">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                          
                          <div className="grid sm:grid-cols-2 gap-4 mb-4">
                            <Input 
                              value={day.title} 
                              onChange={(e) => updateDay(dayIndex, 'title', e.target.value)}
                              placeholder="Day Title (English)"
                            />
                            <Input 
                              value={day.title_ar} 
                              onChange={(e) => updateDay(dayIndex, 'title_ar', e.target.value)}
                              placeholder="عنوان اليوم (Arabic)"
                              dir="rtl"
                            />
                          </div>
                          
                          <div className="grid sm:grid-cols-2 gap-4">
                            <Textarea 
                              value={day.topics?.join('\n') || ''} 
                              onChange={(e) => updateDayTopics(dayIndex, 'topics', e.target.value)}
                              rows={3}
                              placeholder="Topics (one per line)"
                            />
                            <Textarea 
                              value={day.topics_ar?.join('\n') || ''} 
                              onChange={(e) => updateDayTopics(dayIndex, 'topics_ar', e.target.value)}
                              rows={3}
                              placeholder="المواضيع (واحد في كل سطر)"
                              dir="rtl"
                            />
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="btn-primary">Save Template</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCourseTemplatesPage;
